import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { 
  BarChart3,
  Target,
  DollarSign,
  Download,
  Settings,
  X,
  ChevronRight,
  MessageCircle,
  Zap,
  Star,
  LogOut
} from 'lucide-react';
import { clearUser } from '../store/slices/authSlice';
import { useDispatch } from 'react-redux';

const DashboardSidebar = ({ 
  isOpen, 
  onClose, 
  activeSection, 
  onSectionChange, 
  user 
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Sidebar menu items
  const sidebarItems = [
    {
      id: 'dashboard',
      label: 'Dashboard',
      icon: BarChart3,
      color: 'text-[#F2640F]',
      bgColor: 'bg-[#F2640F]/10',
      borderColor: 'border-[#F2640F]/20',
      link: '/dashboard'
    },
    {
      id: 'trending',
      label: 'All Deals',
      icon: Target,
      color: 'text-[#FF0000]',
      bgColor: 'bg-[#FF0000]/10',
      borderColor: 'border-[#FF0000]/20',
      link: '/trending-authenticated'
    },
    {
      id: 'earnings',
      label: 'Earnings',
      icon: DollarSign,
      color: 'text-[#F2640F]',
      bgColor: 'bg-[#F2640F]/10',
      borderColor: 'border-[#F2640F]/20',
      link: '/earnings'
    },
    {
      id: 'profile',
      label: 'Profile',
      icon: Settings,
      color: 'text-[#FF0000]',
      bgColor: 'bg-[#FF0000]/10',
      borderColor: 'border-[#FF0000]/20',
      link: '/profile'
    }
  ];

  // Quick actions for sidebar
  const sidebarQuickActions = [
    {
      id: 'download-report',
      label: 'Download Report',
      icon: Download,
      color: 'text-white',
      bgColor: 'bg-gradient-to-r from-[#FF0000] to-[#F2640F]',
      description: 'Export your earnings data',
      onClick: () => {
        // TODO: Implement download functionality
        console.log('Download report clicked');
      }
    },
    {
      id: 'contact-support',
      label: 'Contact Support',
      icon: MessageCircle,
      color: 'text-white',
      bgColor: 'bg-gradient-to-r from-[#F2640F] to-[#FF0000]',
      description: 'Get help from our team',
      onClick: () => {
        // TODO: Implement contact support functionality
        console.log('Contact support clicked');
      }
    }
  ];

  const handleLogout = () => {
    dispatch(clearUser());
    navigate('/'); // Navigate to home page after logout
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 z-50 backdrop-blur-sm bg-black/50 lg:hidden"
          />
          
          {/* Sidebar */}
          <motion.div
            initial={{ x: '-100%' }}
            animate={{ x: 0 }}
            exit={{ x: '-100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="flex fixed top-0 left-0 z-[60] flex-col w-80 h-full bg-white border-r border-gray-200 shadow-2xl"
          >
            {/* Sidebar Header */}
            <div className="flex justify-between items-center p-6 bg-gradient-to-r from-[#F2640F] to-[#FF0000]">
              <div className="flex gap-3 items-center">
                <div className="p-2 rounded-lg bg-white/20">
                  <Zap className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-white">DealBoxx</h2>
                  <p className="text-xs text-white/80">Promoter Dashboard</p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-2 rounded-lg transition-colors text-white/80 hover:text-white hover:bg-white/10"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* User Profile */}
            <div className="p-6 border-b border-gray-200">
              <div className="flex gap-4 items-center">
                <div className="w-12 h-12 bg-gradient-to-r from-[#F2640F] to-[#FF0000] rounded-full flex items-center justify-center">
                  <span className="text-lg font-bold text-white">
                    {user?.name?.charAt(0) || 'U'}
                  </span>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">{user?.name || 'User'}</h3>
                  <p className="text-sm text-gray-600">{user?.email || 'user@example.com'}</p>
                  <div className="flex gap-1 items-center mt-1">
                    <Star className="w-3 h-3 text-yellow-400 fill-current" />
                    <span className="text-xs text-gray-500">Premium Promoter</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Navigation Menu */}
            <div className="overflow-y-auto flex-1 p-4">
              <nav className="space-y-2">
                {sidebarItems.map((item, index) => (
                  <Link
                    key={item.id}
                    to={item.link}
                    onClick={() => {
                      onSectionChange(item.id);
                      onClose(); // Close sidebar on mobile after navigation
                    }}
                  >
                    <motion.div
                      className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 ${
                        activeSection === item.id
                          ? `${item.bgColor} ${item.borderColor} border shadow-sm`
                          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      }`}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.05 }}
                      whileHover={{ x: 4 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <item.icon className={`w-5 h-5 ${activeSection === item.id ? item.color : 'text-gray-500'}`} />
                      <span className="font-medium">{item.label}</span>
                      {activeSection === item.id && (
                        <ChevronRight className="w-4 h-4 ml-auto text-[#F2640F]" />
                      )}
                    </motion.div>
                  </Link>
                ))}
              </nav>

              {/* Quick Actions Section */}
              <div className="mt-8">
                <h4 className="mb-4 text-sm font-semibold tracking-wider text-gray-500 uppercase">
                  Quick Actions
                </h4>
                <div className="space-y-3">
                  {sidebarQuickActions.map((action, index) => (
                    <motion.button
                      key={action.id}
                      onClick={() => {
                        action.onClick();
                        onClose(); // Close sidebar on mobile after action
                      }}
                      className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left transition-all duration-200 ${action.bgColor} hover:shadow-lg`}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.5 + index * 0.1 }}
                      whileHover={{ scale: 1.02, y: -2 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <action.icon className="w-5 h-5 text-white" />
                      <div>
                        <div className="font-medium text-white">{action.label}</div>
                        <div className="text-xs text-white/80">{action.description}</div>
                      </div>
                    </motion.button>
                  ))}
                </div>
              </div>

            
            </div>

            {/* Sidebar Footer */}
            <div className="p-4 border-t border-gray-200">
              <button onClick={handleLogout} className="flex gap-3 items-center px-4 py-3 w-full text-gray-600 rounded-xl transition-colors hover:text-red-600 hover:bg-red-50">
                <LogOut className="w-5 h-5" />
                <span className="font-medium">Logout</span>
              </button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default DashboardSidebar;

